import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Subscription categories are a settings-managed nomenclature (Settings →
// Categorii abonamente), not a hard-coded enum. `Subscription.category` holds
// the category *code*; labels come from GET /api/subscriptions/categories.
export type SubscriptionCategory = string;

export interface SubscriptionCategoryDef {
  code: string;
  label: string;
}

/** Seed defaults, used as a render fallback while the list loads. */
export const DEFAULT_SUBSCRIPTION_CATEGORIES: SubscriptionCategoryDef[] = [
  { code: 'saas', label: 'SaaS' },
  { code: 'utility', label: 'Utilitate' },
  { code: 'telecom', label: 'Telecom' },
  { code: 'fuel', label: 'Carburant' },
  { code: 'office', label: 'Birou' },
  { code: 'hosting', label: 'Hosting' },
  { code: 'other', label: 'Altele' },
];

/** Code -> label lookup with graceful fallback to the raw code. */
export function subscriptionCategoryLabel(
  code: string,
  categories?: SubscriptionCategoryDef[],
): string {
  const list = categories && categories.length > 0 ? categories : DEFAULT_SUBSCRIPTION_CATEGORIES;
  return list.find((c) => c.code === code)?.label ?? code;
}

export const SUBSCRIPTION_FREQUENCIES = ['monthly', 'quarterly', 'annual', 'usage'] as const;
export type SubscriptionFrequency = (typeof SUBSCRIPTION_FREQUENCIES)[number];

export const SUBSCRIPTION_FREQUENCY_LABELS: Record<SubscriptionFrequency, string> = {
  monthly: 'Lunar',
  quarterly: 'Trimestrial',
  annual: 'Anual',
  usage: 'La consum',
};

export interface Subscription {
  id: string;
  name: string;
  merchantPatterns: string[];
  category: SubscriptionCategory;
  expectedAmount: number | null;
  expectedCurrency: string | null;
  frequency: SubscriptionFrequency;
  // Day of month (1-31) the subscription is charged (cash-flow projection).
  paymentDay: number | null;
  notes: string | null;
  isActive: boolean;
  companyId: string | null;
  company: { id: string; name: string } | null;
  supplierId: string | null;
  supplier: { id: string; name: string } | null;
  // Default expense category preselected on bank-statement reconciliation.
  // Takes priority over the linked supplier's default.
  expenseCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SubscriptionListResponse {
  success: true;
  data: Subscription[];
  meta: SubscriptionListMeta;
}

export interface SubscriptionFilters {
  search?: string;
  active?: 'true' | 'false';
  category?: SubscriptionCategory;
  companyId?: string;
  sort?: SubscriptionSortToken;
  page?: number;
  limit?: number;
}

/** Columns of the subscriptions list that the server can order by. */
export type SubscriptionSortField =
  | 'name'
  | 'category'
  | 'supplier'
  | 'expectedAmount'
  | 'frequency'
  | 'paymentDay'
  | 'isActive'
  | 'createdAt'
  | 'updatedAt';
export type SubscriptionSortToken = SubscriptionSortField | `-${SubscriptionSortField}`;

export interface SubscriptionInput {
  name: string;
  merchantPatterns?: string[];
  category?: SubscriptionCategory;
  expectedAmount?: number | null;
  expectedCurrency?: string | null;
  frequency?: SubscriptionFrequency;
  paymentDay?: number | null;
  notes?: string;
  isActive?: boolean;
  companyId?: string | null;
  supplierId?: string | null;
  // Default expense category; null clears it.
  expenseCategoryId?: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listSubscriptions(
  filters: SubscriptionFilters = {},
): Promise<SubscriptionListResponse> {
  return apiFetch<SubscriptionListResponse>(
    `/api/subscriptions${buildQuery(filters as Record<string, unknown>)}`,
    { headers: authHeaders() },
  );
}

export async function getSubscription(id: string): Promise<Subscription> {
  const res = await apiFetch<Envelope<Subscription>>(`/api/subscriptions/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createSubscription(input: SubscriptionInput): Promise<Subscription> {
  const res = await apiFetch<Envelope<Subscription>>('/api/subscriptions', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateSubscription(
  id: string,
  input: SubscriptionInput,
): Promise<Subscription> {
  const res = await apiFetch<Envelope<Subscription>>(`/api/subscriptions/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function getSubscriptionCategories(): Promise<SubscriptionCategoryDef[]> {
  const res = await apiFetch<Envelope<SubscriptionCategoryDef[]>>('/api/subscriptions/categories', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function updateSubscriptionCategories(
  categories: SubscriptionCategoryDef[],
): Promise<SubscriptionCategoryDef[]> {
  const res = await apiFetch<Envelope<SubscriptionCategoryDef[]>>('/api/subscriptions/categories', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ categories }),
  });
  return res.data;
}

export async function deleteSubscription(id: string): Promise<Subscription> {
  const res = await apiFetch<Envelope<Subscription>>(`/api/subscriptions/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

/** Soft-deletes a batch of subscriptions (isActive=false); unknown ids are skipped. */
export async function bulkDeleteSubscriptions(
  ids: string[],
): Promise<{ requested: number; deactivated: number }> {
  const res = await apiFetch<Envelope<{ requested: number; deactivated: number }>>(
    '/api/subscriptions/bulk-delete',
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }) },
  );
  return res.data;
}
